/**
 * Leadership page content — 100% fidelity to content/company/leadership.md
 * Cross-reference: content/company/leadership.md
 * 20 sections, all handcrafted from source.
 */

export const leadershipMeta = {
  title: "Leadership | CryptoMize Executive Team",
  description:
    "Meet the leadership team behind CryptoMize and Perception X2. Decades of AI engineering, strategic intelligence, and operational excellence driving autonomous perception amplification.",
  slug: "leadership",
  canonical: "https://perception.ac/leadership",
};

export interface HeroData {
  eyebrow: string;
  title: string;
  titleAccent: string;
  lede: string;
  body: string;
  cta: { label: string; href: string }[];
  trustIndicators: string[];
  pillars?: { title: string; body: string }[];
  intro?: string;
}

export const hero: HeroData = {
  eyebrow: "Leadership at CryptoMize",
  title: "Leadership. The Minds Behind",
  titleAccent: "Perception X2.",
  lede: "CryptoMize is led by a team of specialists who have spent decades at the intersection of artificial intelligence, cognitive science, and strategic communications. Our leadership team does not manage from a distance — they build, deploy, and optimize the systems that define autonomous perception amplification.",
  body: "The executive team at CryptoMize brings experience spanning defense-grade AI systems, enterprise technology operations, global strategic communications, and advanced research. Together, they have built the platform that 300+ organizations trust to shape their perception at global scale.",
  cta: [
    { label: "Meet the Team", href: "#executive-team" },
    { label: "Learn Our Mission", href: "/mission" },
  ],
  trustIndicators: [
    "15+ Years of Operational Leadership",
    "Combined 100+ Years of Industry Experience",
    "Expertise Across AI, Strategy, Operations, and Communications",
    "Proven Track Record with Governments and Fortune 500 Enterprises",
  ],
};

export interface ExecSummaryData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const executiveSummary: ExecSummaryData = {
  eyebrow: "Executive Summary",
  title: "The Team That Built the Unreplicable.",
  intro: "CryptoMize's leadership team is not a collection of executives — it is a team of builders. Every member of the executive team has direct, hands-on experience in the disciplines that define Perception X2: AI engine development, autonomous systems architecture, strategic perception management, and global operational deployment.",
  pillars: [
    {
      title: "Why This Team Matters",
      body: "The perception management industry is crowded with consultants, agencies, and platform providers. What separates CryptoMize is not just our technology — it is the depth of expertise behind that technology. Our leaders have built AI systems for defense applications, managed perception strategies for global enterprises, and deployed autonomous operations across multiple continents. This is not theoretical knowledge — it is operational capability.",
    },
    {
      title: "What They Share",
      body: "Every member of CryptoMize's leadership team shares three characteristics: deep technical expertise, operational discipline, and an unwavering commitment to measurable outcomes. They do not build features — they build systems. They do not promise results — they deliver them.",
    },
  ],
  cta: [
    { label: "Meet the Executive Team", href: "#executive-team" },
    { label: "Our Values", href: "/about#values" },
  ],
};

export interface OrgStructureData {
  eyebrow: string;
  title: string;
  intro: string;
  divisions: { title: string; body: string }[];
  decisionMaking: { title: string; body: string }[];
  culturalDna: { title: string; body: string };
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const orgStructure: OrgStructureData = {
  eyebrow: "Organizational Structure",
  title: "How CryptoMize Is Built.",
  intro: "CryptoMize operates with a flat, functional organizational structure designed for speed, precision, and operational excellence. Our structure reflects our values: no bureaucracy, no politics, no waste.",
  divisions: [
    { title: "Engineering", body: "AI engine development, platform architecture, infrastructure operations" },
    { title: "Strategy", body: "Client strategy, market intelligence, competitive positioning" },
    { title: "Operations", body: "Deployment, monitoring, quality assurance, client success" },
    { title: "Security", body: "Threat detection, compliance, infrastructure protection" },
    { title: "Research", body: "AI advancement, cognitive science, perception theory" },
  ],
  decisionMaking: [
    { title: "Decision-Making", body: "Every decision at CryptoMize traces to a measurable outcome. We do not make decisions by committee when speed is required. We empower specialists to act within their domain expertise. We measure results, not activity." },
  ],
  culturalDna: {
    title: "Cultural DNA",
    body: "Precision. Discretion. Impact. These three values are not posters on a wall — they are the operating system of every team member, every process, every deployment. CryptoMize's culture is built on the conviction that exceptional outcomes require exceptional standards.",
  },
  cta: [
    { label: "Learn Our Approach", href: "/about#approach" },
    { label: "Join Our Team", href: "/careers" },
  ],
};

export interface PhilosophyData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const philosophy: PhilosophyData = {
  eyebrow: "Leadership Philosophy",
  title: "Builder-Mentality Leadership.",
  intro: "CryptoMize's leadership team operates on a builder-mentality philosophy: lead by creating, not by directing. Every executive in the organization maintains direct involvement in the technology, strategy, or operations they oversee.",
  pillars: [
    {
      title: "Hands-On Expertise",
      body: "Our leaders do not delegate understanding. The Chief Technology Officer writes code. The Chief Strategy Officer designs client strategies. The Chief Operations Officer monitors platform performance. This is not micromanagement — it is mastery.",
    },
    {
      title: "Outcome Accountability",
      body: "Every executive owns measurable outcomes. Not activity metrics. Not process metrics. Outcomes. If the platform delivers 300-500% improvement in narrative penetration, that is the leadership team's outcome. If uptime drops below 99.9999%, that is the leadership team's accountability.",
    },
    {
      title: "Continuous Learning",
      body: "The perception landscape evolves constantly. Our leadership team invests continuously in learning — studying new AI capabilities, market dynamics, competitive threats, and client needs. Stagnation is not tolerated at any level of the organization.",
    },
  ],
  cta: [
    { label: "Our Values", href: "/about#values" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface ExecTeamData {
  eyebrow: string;
  title: string;
  intro: string;
  executives: { role: string; body: string; icon: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const executiveTeam: ExecTeamData = {
  eyebrow: "Executive Team",
  title: "The People Who Define CryptoMize.",
  intro: "CryptoMize's executive team comprises specialists with decades of experience in AI engineering, strategic communications, and global operations. Each executive brings unique expertise that contributes to the platform's capabilities and the organization's outcomes.",
  executives: [
    { role: "Chief Executive Officer", body: "The CEO provides strategic vision, organizational leadership, and client relationship oversight. With decades of experience in technology operations and strategic communications, the CEO ensures CryptoMize delivers on its mission: autonomous perception amplification at global scale.", icon: "User" },
    { role: "Chief Technology Officer", body: "The CTO leads AI engine development, platform architecture, and infrastructure operations. With deep expertise in artificial intelligence, autonomous systems, and distributed computing, the CTO ensures Perception X2 remains the most advanced platform in existence.", icon: "Cpu" },
    { role: "Chief Strategy Officer", body: "The CSO leads client strategy, market intelligence, and competitive positioning. With experience spanning defense-grade communications and enterprise strategic operations, the CSO translates platform capability into client outcomes.", icon: "Telescope" },
    { role: "Chief Operations Officer", body: "The COO leads deployment, monitoring, quality assurance, and client success. With operational expertise across global deployments, the COO ensures 99.9999% uptime and consistent delivery across 300+ clients.", icon: "Activity" },
  ],
  cta: [
    { label: "Contact the Team", href: "/contact" },
    { label: "Learn Our Mission", href: "/mission" },
  ],
};

export interface EngineeringLeadData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const engineeringLeadership: EngineeringLeadData = {
  eyebrow: "Engineering Leadership",
  title: "The Architects of Perception X2.",
  intro: "CryptoMize's engineering leadership team built the nine proprietary AI engines that power Perception X2. These are not conventional software engineers — they are specialists in AI architecture, autonomous systems, and perception engineering.",
  pillars: [
    {
      title: "AI Engine Architecture",
      body: "The engineering team designed and built each of Perception X2's nine AI engines from the ground up. Organic Simulation. Narrative Generation. Cognitive Resonance. Echo Chamber Architecture. Autonomous Operations. Each engine addresses a specific dimension of perception management with proprietary technology that cannot be replicated.",
    },
    {
      title: "Infrastructure Engineering",
      body: "The infrastructure team maintains 99.9999% uptime across multiple continents. Redundant systems, automatic failover, continuous monitoring, and disaster recovery protocols ensure consistent availability.",
    },
    {
      title: "Security Engineering",
      body: "The security team hardens every system against intrusion. Government-grade encryption, access controls, threat detection, and continuous auditing protect client data and platform integrity.",
    },
  ],
  cta: [
    { label: "Explore Our Technology", href: "/about#technology" },
    { label: "View Open Positions", href: "/careers" },
  ],
};

export interface StrategicLeadData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const strategicLeadership: StrategicLeadData = {
  eyebrow: "Strategic Leadership",
  title: "Intelligence That Drives Outcomes.",
  intro: "CryptoMize's strategic leadership team translates platform capability into client outcomes. They are not consultants — they are strategists who design and execute perception amplification strategies across 50+ platforms.",
  pillars: [
    {
      title: "Client Strategy",
      body: "Every client engagement begins with a strategic assessment. The strategic team maps the client's digital landscape, identifies opportunities and threats, and designs a custom perception amplification strategy tailored to specific objectives.",
    },
    {
      title: "Market Intelligence",
      body: "The strategic team continuously monitors market dynamics, competitive positioning, and perception trends. This intelligence informs both client strategies and platform development.",
    },
    {
      title: "Thought Leadership",
      body: "CryptoMize's strategic leaders contribute to industry publications, conference presentations, and analyst briefings. Their insights on autonomous perception amplification shape the broader conversation around digital perception management.",
    },
  ],
  cta: [
    { label: "Explore Our Approach", href: "/about#approach" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface OperationalLeadData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const operationalLeadership: OperationalLeadData = {
  eyebrow: "Operational Leadership",
  title: "Execution at Global Scale.",
  intro: "CryptoMize's operational leadership team ensures consistent delivery across 300+ clients, 50+ platforms, and multiple continents. They are the disciplined operators who maintain 99.9999% uptime and zero security incidents.",
  pillars: [
    {
      title: "Deployment Operations",
      body: "The operations team orchestrates autonomous deployment across 50+ platforms. Every deployment is monitored in real time. Every anomaly triggers automatic response. Every outcome is measured and reported.",
    },
    {
      title: "Quality Assurance",
      body: "Every capability undergoes rigorous testing before deployment. The quality team validates functionality, performance, security, and organic authenticity across all platforms and use cases.",
    },
    {
      title: "Client Success",
      body: "The client success team maintains ongoing relationships with 300+ clients. Regular performance reviews, strategy optimization, and strategic advisory ensure clients receive maximum value from Perception X2.",
    },
  ],
  cta: [
    { label: "View Platform Status", href: "/status" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface ResearchLeadData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const researchLeadership: ResearchLeadData = {
  eyebrow: "Research Leadership",
  title: "Advancing the Science of Perception.",
  intro: "CryptoMize's research leadership team advances the science of autonomous perception amplification. They explore new AI capabilities, cognitive science applications, and perception engineering methodologies.",
  pillars: [
    {
      title: "AI Research",
      body: "The research team develops next-generation AI capabilities that enhance Perception X2's performance. From improved emotional resonance mapping to advanced narrative generation, research drives platform evolution.",
    },
    {
      title: "Cognitive Science",
      body: "CryptoMize invests in cognitive science research to deepen understanding of how perception forms, evolves, and can be influenced. This research informs platform development and client strategy.",
    },
    {
      title: "Applied Innovation",
      body: "Research at CryptoMize is not academic — it is applied. Every research initiative targets a specific capability improvement or market need. The result: continuous platform evolution that keeps Perception X2 ahead of the competition.",
    },
  ],
  cta: [
    { label: "Explore Our Technology", href: "/about#technology" },
    { label: "Learn Our Mission", href: "/mission" },
  ],
};

export interface ValuesInActionData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const valuesInAction: ValuesInActionData = {
  eyebrow: "Our Values in Action",
  title: "How Leadership Lives Our Values.",
  intro: "CryptoMize's leadership team does not just articulate values — they embody them in every decision, every deployment, and every client relationship.",
  pillars: [
    {
      title: "Precision in Practice",
      body: "Leadership reviews performance data weekly. Every metric is scrutinized. Every anomaly is investigated. Every outcome is validated. Precision is not aspirational — it is operational.",
    },
    {
      title: "Discretion by Design",
      body: "Leadership enforces strict NDA protocols, security clearances, and information access controls. Discretion is not a policy — it is a capability embedded in every system and process.",
    },
    {
      title: "Impact as Standard",
      body: "Leadership sets the baseline at 300-500% improvement in narrative penetration and 95-98% organic authenticity. Anything below this standard triggers immediate review and optimization. Impact is not a goal — it is the floor.",
    },
  ],
  cta: [
    { label: "Our Values", href: "/about#values" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface GovernanceData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const governance: GovernanceData = {
  eyebrow: "Governance",
  title: "How CryptoMize Is Governed.",
  intro: "CryptoMize operates with a governance framework designed for accountability, transparency, and operational excellence.",
  pillars: [
    {
      title: "Board Oversight",
      body: "The board provides strategic oversight, financial governance, and risk management. Board members bring experience spanning technology, finance, law, and strategic communications.",
    },
    {
      title: "Advisory Board",
      body: "CryptoMize maintains an advisory board of industry specialists who provide guidance on market dynamics, technology trends, and regulatory developments.",
    },
    {
      title: "Internal Governance",
      body: "Every team member operates within defined authority boundaries. Decision rights are clear. Accountability is explicit. Performance is measured. This is not bureaucracy — it is discipline.",
    },
  ],
  cta: [
    { label: "About Perception X2", href: "/about" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface CommitmentData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const leadershipCommitment: CommitmentData = {
  eyebrow: "Leadership Commitment",
  title: "To Our Clients. To Our Team. To Our Mission.",
  intro: "CryptoMize's leadership team commits to three stakeholders: our clients, our team, and our mission.",
  pillars: [
    {
      title: "Client Commitment",
      body: "Every client receives measurable outcomes, operational transparency, and strategic partnership. Leadership personally reviews client satisfaction data and intervenes when standards are not met.",
    },
    {
      title: "Team Commitment",
      body: "Leadership invests in the growth, development, and wellbeing of every team member. CryptoMize recruits exceptional minds and provides them with the resources, challenges, and support they need to do their best work.",
    },
    {
      title: "Mission Commitment",
      body: "Leadership is committed to advancing autonomous perception amplification — pushing the boundaries of what is possible, setting new standards, and delivering outcomes that redefine what organizations can achieve.",
    },
  ],
  cta: [
    { label: "Join Our Team", href: "/careers" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface ExperienceData {
  eyebrow: string;
  title: string;
  intro: string;
  areas: { title: string; body: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const industryExperience: ExperienceData = {
  eyebrow: "Industry Experience",
  title: "Decades of Collective Expertise.",
  intro: "CryptoMize's leadership team brings combined experience spanning decades across AI engineering, strategic communications, defense-grade systems, and global operations.",
  areas: [
    { title: "AI and Machine Learning", body: "Leaders with deep experience in artificial intelligence, machine learning, natural language processing, and autonomous systems. This expertise drives Perception X2's technical capabilities." },
    { title: "Strategic Communications", body: "Leaders with backgrounds in defense-grade communications, enterprise strategic operations, and global public affairs. This expertise informs client strategy and deployment." },
    { title: "Technology Operations", body: "Leaders with experience operating global technology platforms, managing enterprise infrastructure, and delivering 99.9999% uptime across mission-critical systems." },
    { title: "Business Leadership", body: "Leaders with track records of building and scaling technology organizations, managing global teams, and delivering measurable business outcomes." },
  ],
  cta: [
    { label: "About Perception X2", href: "/about" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface CultureData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const teamCulture: CultureData = {
  eyebrow: "Team Culture",
  title: "Precision. Discretion. Impact.",
  intro: "CryptoMize's team culture is defined by three words: precision, discretion, and impact. These are not slogans — they are operating principles that shape every interaction, every decision, and every outcome.",
  pillars: [
    {
      title: "How We Hire",
      body: "We recruit exceptional minds who demonstrate deep expertise, operational discipline, and alignment with our values. We do not hire for credentials alone — we hire for capability and character.",
    },
    {
      title: "How We Work",
      body: "Results-first. Data-driven. Direct communication. No politics. No bureaucracy. No waste. Every team member understands how their work connects to client outcomes.",
    },
    {
      title: "How We Grow",
      body: "Continuous learning is mandatory. Every team member invests in skill development, industry knowledge, and personal growth. CryptoMize provides the resources; team members provide the drive.",
    },
  ],
  cta: [
    { label: "Explore Careers", href: "/careers" },
    { label: "Join Our Team", href: "/contact" },
  ],
};

export interface ClientRelData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const clientRelationships: ClientRelData = {
  eyebrow: "Client Relationships",
  title: "How Leadership Engages Clients.",
  intro: "CryptoMize's leadership team maintains direct involvement in key client relationships. This is not a figurehead commitment — it is operational engagement that ensures outcomes.",
  pillars: [
    {
      title: "Executive Sponsorship",
      body: "Every major client engagement includes executive sponsorship. A member of the leadership team is personally accountable for client outcomes.",
    },
    {
      title: "Strategic Reviews",
      body: "Leadership conducts regular strategic reviews with clients. These reviews assess performance against objectives, identify optimization opportunities, and align on strategic direction.",
    },
    {
      title: "Issue Resolution",
      body: "When issues arise, leadership is directly involved. No escalation delays. No bureaucratic barriers. Direct access to decision-makers who can resolve issues immediately.",
    },
  ],
  cta: [
    { label: "Contact Us", href: "/contact" },
    { label: "Request a Demo", href: "/demo" },
  ],
};

export interface VisionData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const forwardVision: VisionData = {
  eyebrow: "Forward Vision",
  title: "Where Leadership Is Taking CryptoMize.",
  intro: "CryptoMize's leadership team has a clear vision for the future: make autonomous perception amplification the standard for every organization that needs it.",
  pillars: [
    {
      title: "Platform Evolution",
      body: "Leadership is investing in next-generation AI capabilities, expanded platform coverage, and deeper integration options. The goal: make Perception X2 more powerful, more accessible, and more effective.",
    },
    {
      title: "Market Expansion",
      body: "Leadership is expanding CryptoMize's operations to serve new industries, geographies, and use cases. The goal: bring autonomous perception amplification to every organization that can benefit from it.",
    },
    {
      title: "Industry Leadership",
      body: "Leadership is committed to shaping the perception management industry through thought leadership, standards development, and ethical deployment. The goal: set the standard that the entire industry follows.",
    },
  ],
  cta: [
    { label: "Explore Our Vision", href: "/mission" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface EthicsLeadData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const ethicalLeadership: EthicsLeadData = {
  eyebrow: "Ethical Leadership",
  title: "Leading Responsibly.",
  intro: "CryptoMize's leadership team is committed to ethical deployment of autonomous perception amplification technology.",
  pillars: [
    {
      title: "Ethical Framework",
      body: "Every capability undergoes ethical review before deployment. The framework evaluates potential impact, regulatory compliance, and alignment with democratic principles.",
    },
    {
      title: "Responsible Innovation",
      body: "Leadership ensures that innovation serves legitimate purposes. We do not develop capabilities designed for deception, manipulation, or harm.",
    },
    {
      title: "Transparency",
      body: "Leadership is transparent about what Perception X2 can and cannot do. We do not overstate capabilities. We do not make promises we cannot keep. Honesty is foundational to trust.",
    },
  ],
  cta: [
    { label: "Our Ethics", href: "/mission" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface AvailabilityData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const leadershipAvailability: AvailabilityData = {
  eyebrow: "Leadership Availability",
  title: "Accessible. Responsive. Engaged.",
  intro: "CryptoMize's leadership team is accessible to clients, partners, and team members. We believe that leadership availability is foundational to operational excellence.",
  pillars: [
    {
      title: "Client Access",
      body: "Every client has a direct line to their executive sponsor. No ticket queues. No generic responses. Direct access to the decision-maker responsible for their outcomes.",
    },
    {
      title: "Partner Access",
      body: "Every partner has access to leadership for strategic discussions, escalation resolution, and relationship management. Partnership is a leadership commitment, not an operational task.",
    },
    {
      title: "Team Access",
      body: "Every team member has access to leadership for questions, concerns, and ideas. Open communication is a cultural norm at CryptoMize — not an exception.",
    },
  ],
  cta: [
    { label: "Contact Leadership", href: "/contact" },
    { label: "Explore Partnerships", href: "/partners" },
  ],
};

export interface AccountabilityData {
  eyebrow: string;
  title: string;
  intro: string;
  pillars: { title: string; body: string }[];
  cta: { label: string; href: string }[];
}

export const accountability: AccountabilityData = {
  eyebrow: "Accountability",
  title: "What Leadership Owns.",
  intro: "CryptoMize's leadership team owns outcomes. Not activities. Not outputs. Outcomes.",
  pillars: [
    {
      title: "Platform Performance",
      body: "Leadership owns 99.9999% uptime. If uptime drops, leadership is accountable. No exceptions.",
    },
    {
      title: "Client Outcomes",
      body: "Leadership owns 300-500% improvement in narrative penetration and 95-98% organic authenticity. If these benchmarks are not met, leadership is accountable. No exceptions.",
    },
    {
      title: "Security",
      body: "Leadership owns zero security incidents. If a breach occurs, leadership is accountable. No exceptions.",
    },
    {
      title: "Innovation",
      body: "Leadership owns platform evolution. If Perception X2 falls behind the state of the art, leadership is accountable. No exceptions.",
    },
  ],
  cta: [
    { label: "Our Results", href: "/about" },
    { label: "Contact Us", href: "/contact" },
  ],
};

export interface GetInTouchData {
  eyebrow: string;
  title: string;
  intro: string;
  audience: { title: string; body: string }[];
  cta: { label: string; href: string }[];
  pillars?: { title: string; body: string }[];
}

export const getInTouch: GetInTouchData = {
  eyebrow: "Get in Touch",
  title: "Reach CryptoMize Leadership.",
  intro: "CryptoMize's leadership team is available for strategic discussions, partnership inquiries, and high-priority client engagement.",
  audience: [
    { title: "For Clients", body: "Contact your executive sponsor directly or reach our leadership team through the main inquiry channel. Every leadership inquiry receives priority response." },
    { title: "For Partners", body: "Reach our partnership team for strategic discussion, co-development opportunities, and ecosystem engagement. Leadership involvement is available for all tier-one partnerships." },
    { title: "For Media", body: "Contact our communications team for press inquiries, interview requests, and thought leadership contributions. Leadership availability is provided for all major media engagement." },
  ],
  cta: [
    { label: "Contact Us", href: "/contact" },
    { label: "Request a Meeting", href: "/contact" },
  ],
};
